import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductPrice extends HTMLElement {
  #observer = null;
  #correcting = false;

  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#startObserver();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  // Watch for any external DOM changes and re-apply the category price override.
  // Uses a #correcting flag instead of stop/start to avoid a blind-spot window.
  #startObserver() {
    if (this.#observer) return;
    if (!(this instanceof Node)) return; // guard against non-Node context (browser extensions, iframes)
    this.#observer = new MutationObserver(() => {
      if (this.#correcting) return;
      this.#correcting = true;
      const variantPicker = this.closest('[data-product]')?.querySelector('variant-picker');
      variantPicker?.updateOverridePrice?.();
      setTimeout(() => { this.#correcting = false; }, 50);
    });
    // Watch childList + characterData for content changes only.
    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  /**
   * Updates the price.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newPrice = event.detail.data.html.querySelector('product-price [ref="priceContainer"]');
    const currentPrice = this.querySelector('[ref="priceContainer"]');

    if (!newPrice || !currentPrice) return;

    if (currentPrice.innerHTML !== newPrice.innerHTML) {
      this.#correcting = true;
      currentPrice.replaceWith(newPrice);
      setTimeout(() => {
        const variantPicker = this.closest('[data-product]')?.querySelector('variant-picker');
        if (variantPicker && typeof variantPicker.updateOverridePrice === 'function') {
          variantPicker.updateOverridePrice();
        }
        setTimeout(() => { this.#correcting = false; }, 50);
      }, 0);
    }

    // Toggle native preorder badge using inventory data from the variant event.
    const vd = event.detail?.resource;
    if (vd && typeof vd.inventory_management !== 'undefined') {
      const isPreorder =
        vd.inventory_management === 'shopify' &&
        Number(vd.inventory_quantity) <= 0 &&
        vd.inventory_policy === 'continue';
      this.dataset.isPreorder = isPreorder ? 'true' : 'false';
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
